import React from "react";
import { Box, Divider, Popover, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import axios from "../../utils/axios.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import { tallyTokens } from "../../theme.tsx";
import ComposerActionButton from "./ComposerActionButton.tsx";
import { SourcesIcon } from "../icons/ComposerIcons.tsx";

interface ConnectedSource {
  identifier: string;
  display_name: string;
}

interface Props {
  disabled?: boolean;
}

/**
 * Shows how many systems an answer can draw on, and which ones.
 *
 * Backed by `GET /v1/integrations/connected/`, a single aggregate - the
 * per-integration status endpoints would mean five requests on every
 * conversation view to answer the same question.
 */
// The composer remounts every time the conversation view comes back, and each
// mount used to wait for the request before the button appeared. The last
// answer is kept per session token, so the button shows at once and is
// refreshed quietly behind it.
let cachedSources: { token: string; sources: ConnectedSource[] } | null = null;

const SourcesButton: React.FC<Props> = ({ disabled }) => {
  const navigate = useNavigate();
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);
  const loginToken = useTypedSelector((state) => state.authState.loginToken);
  const [sources, setSources] = React.useState<ConnectedSource[] | null>(() =>
    cachedSources && cachedSources.token === loginToken
      ? cachedSources.sources
      : null,
  );
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!isLoggedIn) {
      cachedSources = null;
      setSources(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get("/v1/integrations/connected/");
        if (response.status !== 200 || !response.data) return;
        const fresh: ConnectedSource[] = response.data.sources ?? [];
        cachedSources = { token: loginToken, sources: fresh };
        if (!cancelled) setSources(fresh);
      } catch (error) {
        console.error("Error fetching connected sources:", error);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, loginToken]);

  // Nothing useful to say until the first response lands.
  if (!isLoggedIn || sources === null) return null;

  const handleManage = () => {
    setAnchorEl(null);
    navigate("/integrations");
  };

  return (
    <React.Fragment>
      <ComposerActionButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        aria-label={i18n._(
          msg({
            message: "Show connected sources",
            context:
              "Accessible name for the composer button listing connected data sources",
          }),
        )}
        icon={<SourcesIcon />}
        label={
          <React.Fragment>
            <Trans context="Composer action listing the connected data sources">
              Sources
            </Trans>
            {` · ${sources.length}`}
          </React.Fragment>
        }
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: "-8px",
              minWidth: 232,
              borderRadius: `${tallyTokens.radius.md}px`,
              border: `1px solid ${tallyTokens.line}`,
              boxShadow: tallyTokens.shadow.lg,
              backgroundColor: tallyTokens.surface,
            },
          },
        }}
      >
        <Box sx={{ padding: "12px 14px 8px" }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              color: tallyTokens.ink3,
              textAlign: "left",
            }}
          >
            <Trans context="Heading above the list of connected data sources">
              Answers can use
            </Trans>
          </Typography>
        </Box>

        <Box sx={{ padding: "0 14px 10px" }}>
          {sources.length === 0 ? (
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: tallyTokens.ink2,
                textAlign: "left",
              }}
            >
              <Trans context="Shown when no data sources are connected yet">
                No systems connected yet.
              </Trans>
            </Typography>
          ) : (
            sources.map((source) => (
              <Box
                key={source.identifier}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "9px",
                  padding: "5px 0",
                }}
              >
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "3px",
                    backgroundColor: tallyTokens.good,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{ fontSize: "0.875rem", color: tallyTokens.ink }}
                >
                  {source.display_name}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Divider sx={{ borderColor: tallyTokens.line }} />

        <Box sx={{ padding: "6px" }}>
          <ComposerActionButton
            onClick={handleManage}
            icon={<SourcesIcon />}
            label={
              <Trans context="Link from the sources list to the integrations page">
                Manage integrations
              </Trans>
            }
          />
        </Box>
      </Popover>
    </React.Fragment>
  );
};

export default SourcesButton;
